/* === BEGIN imported: customComponent/hopnPerks.jsx (verbatim from plugin) === */
import React, {useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Modal from "react-modal";
import {motion, AnimatePresence} from "framer-motion";
import {hopnLanguageArray} from "@/utils/functions.js";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {isNearAirport} from "@/utils/airports";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {squareBigFav, infoIcon, bigLockIcon, afterPayImg, arrowRightSlim} from "@img/index.js";
import {AirportPickupPerks} from "../modalPages/airportPickupPerks.jsx";
import {FindingDriver} from "../modalPages/findingDriver.jsx";

function PopupOverlay({isOpen, onClose, children}) {
    if (!isOpen) return null;
    return (
        <div className="hopn-modal-overlay" onClick={onClose}>
            <div className="hopn-modal-container" onClick={e => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
}

// sliderOnly → just the perk slider (no "More Perks" link).
// linkOnly    → the inverse: just the "More Perks" link, no slider. Used by the
//               destination banner, which shows the link over the map without the
//               perk carousel. The booking modal steps still render the slider.
export function HopnPerks({
    onMorePerksClick,
    sliderOnly = false,
    linkOnly = false,
    modalStyle = "modal",
    slideOrder = ["square", "flatRate", "afterPay"],
    infoHandlers = {},
}) {
    const [comingSoonPopup, setComingSoonPopup] = useState(false);
    const [airportPerksPopup, setAirportPerksPopup] = useState(false);
    const [airportPerksView, setAirportPerksView] = useState('perks'); // 'perks' | 'findingDriver'

    // Detect airport trip from destinations in sessionStorage
    const destinations = storage.get(STORAGE_KEYS.DESTINATIONS, []);
    const firstDest = destinations[0];
    const lastDest = destinations[destinations.length - 1];
    const isAirportTrip = (firstDest?.lat && firstDest?.lng ? !!isNearAirport(firstDest.lat, firstDest.lng) : false)
        || (lastDest?.lat && lastDest?.lng ? !!isNearAirport(lastDest.lat, lastDest.lng) : false);

    const useModal = modalStyle === "modal";

    const handleSlideClick = (key) => {
        const handler = infoHandlers[key];
        if (handler) {
            window.innerWidth >= 768 ? handler.desktop() : handler.mobile();
        } else {
            setComingSoonPopup(true);
        }
    };

    const onSwiperClick = (swiper, event) => {
        const perk = event.target.closest('[data-perk-key]');
        if (perk) handleSlideClick(perk.dataset.perkKey);
    };

    const slides = {
        square: (
            <SwiperSlide key="square">
                <div className="hopn-payment-perks" data-perk-key="square">
                    <span className="after-pay">
                        <img src={squareBigFav} alt="Pay after drop off"/>
                    </span>
                    <div className="content">
                        <h5>Pay after drop off</h5>
                        <p>Square Holds Funds</p>
                    </div>
                    <span className="info-button">
                        <img decoding="async" src={infoIcon} alt="Info"/>
                    </span>
                </div>
            </SwiperSlide>
        ),
        flatRate: (
            <SwiperSlide key="flatRate">
                <div className="hopn-payment-perks" data-perk-key="flatRate">
                    <span className="after-pay">
                        <img src={bigLockIcon} alt="Guaranteed Flat Rate"/>
                    </span>
                    <div className="content">
                        <h5>Guaranteed Flat Rate</h5>
                        <p>No Surge. No KM or Minute Charges</p>
                    </div>
                    <span className="info-button">
                        <img decoding="async" src={infoIcon} alt="Info"/>
                    </span>
                </div>
            </SwiperSlide>
        ),
        afterPay: (
            <SwiperSlide key="afterPay">
                <div className="hopn-payment-perks" data-perk-key="afterPay">
                    <span className="after-pay">
                        <img src={afterPayImg} alt="Ride now Pay later"/>
                    </span>
                    <div className="content">
                        <h5>Ride now Pay later</h5>
                        <p>4 Interest-free payments</p>
                    </div>
                    <span className="info-button">
                        <img decoding="async" src={infoIcon} alt="Info"/>
                    </span>
                </div>
            </SwiperSlide>
        ),
    };

    const comingSoonContent = (
        <>
            <ModalHeader
                inline={true}
                title=""
                extraClasses=""
                totalSteps={0}
                cancelButton={true}
                cancelButtonType="icon"
                ignoreEditMode={true}
                showBackButton={false}
                setStep={() => {}}
                closeModal={() => setComingSoonPopup(false)}
            />
            <div className="hopn-coming-soon-info modalPages-spacing pb-5 text-center">
                <img src={infoIcon} alt="Coming Soon"/>
                <h3>Coming Soon</h3>
                <p>This feature is currently under development and will be available shortly.</p>
            </div>
        </>
    );

    const airportPerksContent = (
        <div className="hopn-airport-perks-modal">
            <ModalHeader
                inline={true}
                title=""
                extraClasses="less-space"
                totalSteps={0}
                cancelButton={true}
                cancelButtonType="text"
                ignoreEditMode={true}
                showBackButton={airportPerksView === 'findingDriver'}
                backStep="perks"
                setStep={() => setAirportPerksView('perks')}
                closeModal={() => { setAirportPerksPopup(false); setAirportPerksView('perks'); }}
            />
            <AnimatePresence mode="wait">
                <motion.div
                    key={airportPerksView}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.15}}
                >
                    {airportPerksView === 'findingDriver' ? (
                        <FindingDriver
                            setStep={() => setAirportPerksView('perks')}
                            closeModal={() => { setAirportPerksPopup(false); setAirportPerksView('perks'); }}
                            hideHeader={true}
                        />
                    ) : (
                        <AirportPickupPerks
                            setStep={(step) => {
                                if (step === 'findingDriver') {
                                    setAirportPerksView('findingDriver');
                                } else {
                                    setAirportPerksPopup(false);
                                    setAirportPerksView('perks');
                                }
                            }}
                            closeModal={() => { setAirportPerksPopup(false); setAirportPerksView('perks'); }}
                            hideHeader={true}
                        />
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );

    return (
        <>
            {useModal ? (
                <Modal
                    isOpen={comingSoonPopup}
                    onRequestClose={() => setComingSoonPopup(false)}
                    overlayClassName="hopn-modal-overlay"
                    className="hopn-modal-container"
                    shouldCloseOnOverlayClick={false}
                >
                    {comingSoonContent}
                </Modal>
            ) : (
                <PopupOverlay isOpen={comingSoonPopup} onClose={() => setComingSoonPopup(false)}>
                    {comingSoonContent}
                </PopupOverlay>
            )}

            {useModal ? (
                <Modal
                    isOpen={airportPerksPopup}
                    onRequestClose={() => { setAirportPerksPopup(false); setAirportPerksView('perks'); }}
                    overlayClassName="hopn-modal-overlay"
                    className="hopn-modal-container"
                    shouldCloseOnOverlayClick={false}
                >
                    {airportPerksContent}
                </Modal>
            ) : (
                <PopupOverlay isOpen={airportPerksPopup} onClose={() => { setAirportPerksPopup(false); setAirportPerksView('perks'); }}>
                    {airportPerksContent}
                </PopupOverlay>
            )}

            {!linkOnly && (
                <div className="hopn-luggage-type-selector">
                    {/* Fixed counts rather than "auto": the perks are a two-card set now,
                        so desktop shows both side by side and mobile shows one with the
                        next peeking (1.2) to signal the swipe. */}
                    <Swiper
                        modules={[Autoplay]}
                        spaceBetween={16}
                        style={{width: "100%"}}
                        slidesPerView={1.2}
                        breakpoints={{768: {slidesPerView: 2}}}
                        freeMode={true}
                        autoplay={{delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true}}
                        loop={false}
                        onClick={onSwiperClick}
                    >
                        {slideOrder.map(key => slides[key])}
                    </Swiper>
                </div>
            )}
            {!sliderOnly && (
                <div className="hopn-quick-service-link">
                    <button onClick={isAirportTrip ? () => setAirportPerksPopup(true) : onMorePerksClick}>
                        {isAirportTrip
                            ? hopnLanguageArray("hopn-perks", "more_perks", "More airport pickup Perks")
                            : "More Perks"}
                        <span>
                            <img
                                src={arrowRightSlim}
                                alt="Arrow right"
                            />
                        </span>
                    </button>
                </div>
            )}
        </>
    );
}
/* === END imported: customComponent/hopnPerks.jsx === */
